/**
 * Connector Storage Utility
 *
 * Manages connector state in localStorage for persistence across sessions.
 * Used to track connector status between frontend state and backend data.
 */

import type { ConnectorLocalState } from '../services/types';

const CONNECTOR_STORAGE_KEY = 'good-outcomes:connector:dataeq';

/**
 * Save connector state to localStorage
 *
 * @param state - Connector state to persist
 */
export const saveConnectorState = (state: ConnectorLocalState): void => {
  try {
    localStorage.setItem(CONNECTOR_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save connector state to localStorage:', error);
  }
};

/**
 * Load connector state from localStorage
 *
 * @returns ConnectorLocalState if exists, null otherwise
 */
export const loadConnectorState = (): ConnectorLocalState | null => {
  try {
    const stored = localStorage.getItem(CONNECTOR_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const state = JSON.parse(stored) as ConnectorLocalState;

    // Validate the loaded state
    if (!state.status || !state.lastUpdated) {
      console.warn('Invalid connector state in localStorage, clearing...');
      clearConnectorState();
      return null;
    }

    return state;
  } catch (error) {
    console.error('Failed to load connector state from localStorage:', error);
    clearConnectorState();
    return null;
  }
};

/**
 * Clear connector state from localStorage
 */
export const clearConnectorState = (): void => {
  try {
    localStorage.removeItem(CONNECTOR_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear connector state from localStorage:', error);
  }
};

/**
 * Update connector status in localStorage
 *
 * @param status - New connector status
 */
export const updateConnectorStatus = (
  status: ConnectorLocalState['status']
): void => {
  const state: ConnectorLocalState = {
    status,
    lastUpdated: new Date().toISOString(),
  };
  saveConnectorState(state);
};

/**
 * Check if connector is configured (has state in localStorage)
 *
 * @returns true if connector state exists
 */
export const isConnectorConfigured = (): boolean => {
  const state = loadConnectorState();
  return state !== null && state.status !== 'not_configured';
};